import { safeJson } from "src/app/helpers/Index";
import { faLock, faTrash } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputNumber } from "primereact/inputnumber";
import { InputSwitch } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import React from "react";
import { AllocationService } from "src/app/services/Index";
import "./Allocations.css";

type Props = {
	/** OrderProductType: 0 Fixture, 10 Event */
	parentType: number;
	parentId: number;
	onError?: (message: string) => void;
	onInfo?: (message: string) => void;
};

type State = {
	allocations: any[];
	isLoading: boolean;
	saving: boolean;
	showDialog: boolean;
	edit: any;
};

/** Codes get read down the phone to an away ticket office, so keep them sayable. */
const suggestCode = () => {
	const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";   // no O/0, no I/1
	let code = "";
	for (let i = 0; i < 6; i++) code += alphabet[Math.floor(Math.random() * alphabet.length)];
	return code;
};

/**
 * Allocations on a fixture: private, capped, code-gated pools.
 *
 * Not an away-ticket screen. The away club's allocation is one row here,
 * alongside sponsor blocks, supporters' clubs and school groups — which is why
 * the fields are name, cap, discount and code rather than anything about away
 * fans.
 */
export class Allocations extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = { allocations: [], isLoading: true, saving: false, showDialog: false, edit: null };
	}

	componentDidMount() {
		this.load();
	}

	componentDidUpdate(prev: Props) {
		if (prev.parentId !== this.props.parentId) this.load();
	}

	async load() {
		try {
			const allocations = await safeJson(AllocationService.getForParent(this.props.parentType, this.props.parentId));
			this.setState({ allocations: Array.isArray(allocations) ? allocations : [], isLoading: false });
		} catch {
			this.setState({ allocations: [], isLoading: false });
		}
	}

	open(allocation?: any) {
		this.setState({
			edit: allocation
				? { ...allocation }
				: { accessCode: suggestCode(), discountPercent: 0, isOpen: true, name: "", ticketLimit: 100 },
			showDialog: true
		});
	}

	async save() {
		const a = this.state.edit;
		if (!a?.name?.trim()) return;

		this.setState({ saving: true });
		const payload = {
			accessCode: (a.accessCode || "").trim() || null,
			discountPercent: Number(a.discountPercent) || 0,
			isOpen: !!a.isOpen,
			maxPerOrder: a.maxPerOrder || null,
			name: a.name.trim(),
			notes: a.notes || null,
			ticketLimit: Number(a.ticketLimit) || 0
		};

		const res = a.id
			? await AllocationService.update(a.id, payload)
			: await AllocationService.add(this.props.parentType, this.props.parentId, payload);
		this.setState({ saving: false });

		if (!res.ok) {
			const message = await res.text();
			this.props.onError?.(message || "Couldn't save that allocation.");
			return;
		}
		this.setState({ edit: null, showDialog: false });
		this.load();
	}

	async remove(allocation: any) {
		if (!globalThis.confirm(
			`Remove "${allocation.name}"? Its ${allocation.remaining} unsold tickets go back to general sale. Anything already bought stays valid.`
		)) return;
		await AllocationService.remove(allocation.id);
		this.load();
	}

	copyCode(code: string) {
		globalThis.navigator?.clipboard?.writeText(code);
		this.props.onInfo?.(`Code ${code} copied.`);
	}

	render() {
		const { allocations, isLoading } = this.state;
		const edit = this.state.edit;
		const held = allocations.reduce((sum, a) => sum + (Number(a.ticketLimit) || 0), 0);

		return (
			<div className="al-wrap">
				<div className="al-head">
					<h4>Allocations</h4>
					<Button className="p-button-text" icon="pi pi-plus" label="Add allocation" onClick={() => this.open()} />
				</div>

				{isLoading && <p className="al-none">Loading&hellip;</p>}

				{!isLoading && allocations.length === 0 &&
					<div className="al-empty">
						<FontAwesomeIcon icon={faLock} />
						<p>
							No allocations. Set one up to hold tickets back for the away club, a sponsor,
							a supporters&rsquo; club or a school group &mdash; they buy with a code, capped at whatever you say.
						</p>
					</div>}

				{allocations.length > 0 && <>
					<div className="al-table">
						<div className="al-tr al-th">
							<span>Name</span><span>Sold</span><span>Discount</span><span>Code</span><span>Status</span><span />
						</div>
						{allocations.map((a) => (
							<div className="al-tr" key={`a-${a.id}`}>
								<span>
									{a.name}
									{a.maxPerOrder && <small>max {a.maxPerOrder} per person</small>}
								</span>
								<span>
									<strong>{a.sold}</strong> / {a.ticketLimit}
									<small>{a.remaining} left</small>
								</span>
								<span>{Number(a.discountPercent) > 0 ? `${Number(a.discountPercent)}%` : <em className="al-muted">none</em>}</span>
								<span>
									{a.accessCode
										? <button className="al-code" onClick={() => this.copyCode(a.accessCode)} title="Copy" type="button">{a.accessCode}</button>
										: <em className="al-muted">no code</em>}
								</span>
								<span className={a.isOpen ? "al-open" : "al-shut"}>{a.isOpen ? "Open" : "Held"}</span>
								<span className="al-actions">
									<Button className="p-button-text" label="Edit" onClick={() => this.open(a)} style={{ padding: "2px 6px" }} />
									<Button className="p-button-text p-button-danger" icon={<FontAwesomeIcon icon={faTrash} />} onClick={() => this.remove(a)} style={{ padding: "2px 6px" }} />
								</span>
							</div>
						))}
					</div>
					<p className="al-total">{held} tickets held back from general sale across {allocations.length} allocation{allocations.length === 1 ? "" : "s"}.</p>
				</>}

				<Dialog footer={<>
					<Button className="p-button-text" label="Cancel" onClick={() => this.setState({ showDialog: false })} />
					<Button disabled={this.state.saving} icon="pi pi-check" label={this.state.saving ? "Saving…" : "Save"} onClick={() => this.save()} />
				</>} header={edit?.id ? "Edit allocation" : "New allocation"} modal onHide={() => this.setState({ showDialog: false })} style={{ width: 480 }} visible={this.state.showDialog}>
					{edit && <>
						<label className="al-label">Who is it for</label>
						<InputText autoFocus onChange={(e) => this.setState({ edit: { ...edit, name: e.target.value } })} placeholder="Yeovil Town away allocation" style={{ marginBottom: 16, width: "100%" }} value={edit.name || ""} />

						<div className="al-two">
							<div>
								<label className="al-label">How many tickets</label>
								<InputNumber onValueChange={(e) => this.setState({ edit: { ...edit, ticketLimit: e.value } })} style={{ width: "100%" }} value={edit.ticketLimit} />
							</div>
							<div>
								<label className="al-label">Discount</label>
								<InputNumber max={100} min={0} onValueChange={(e) => this.setState({ edit: { ...edit, discountPercent: e.value } })} suffix="%" style={{ width: "100%" }} value={edit.discountPercent} />
							</div>
						</div>
						<p className="al-hint">These come out of the fixture&rsquo;s capacity and aren&rsquo;t offered in general sale.</p>

						<label className="al-label" style={{ marginTop: 16 }}>Access code</label>
						<div className="al-two">
							<InputText onChange={(e) => this.setState({ edit: { ...edit, accessCode: e.target.value.toUpperCase() } })} style={{ flex: 1 }} value={edit.accessCode || ""} />
							<Button className="p-button-outlined" label="New code" onClick={() => this.setState({ edit: { ...edit, accessCode: suggestCode() } })} />
						</div>
						<p className="al-hint">Whoever has this code can buy from the pool. Leave it blank and only you can sell these.</p>

						<label className="al-label" style={{ marginTop: 16 }}>Most one person can buy (optional)</label>
						<InputNumber onValueChange={(e) => this.setState({ edit: { ...edit, maxPerOrder: e.value } })} placeholder="No limit" style={{ width: "100%" }} value={edit.maxPerOrder} />

						<div className="al-switch" style={{ marginTop: 18 }}>
							<InputSwitch checked={!!edit.isOpen} onChange={(e) => this.setState({ edit: { ...edit, isOpen: e.value } })} />
							<label>Open for buying</label>
						</div>
						<p className="al-hint">Turn it off to hold the tickets without selling them yet.</p>

						<label className="al-label" style={{ marginTop: 16 }}>Notes (optional)</label>
						<InputText onChange={(e) => this.setState({ edit: { ...edit, notes: e.target.value } })} placeholder="Who agreed it, and when" style={{ width: "100%" }} value={edit.notes || ""} />
					</>}
				</Dialog>
			</div>
		);
	}
}
